import { DateTime } from 'luxon'

export const isValidTimeString = (time: string): boolean => {
  return (
    time
      .trim()
      .toLowerCase()
      .match(/^([01]?[0-9]|2[0-3]):[0-5][0-9] (A|a|P|p)(M|m)$/) !== null
  )
}

export const isValidIntervalString = (interval: string): boolean => {
  return (
    interval
      .trim()
      .toLowerCase()
      .match(/^(\d+)hr ([0-5]?[0-9])min$/) !== null
  )
}

/**
 * Returns a human readable string giving a date and time in a given timezone.
 * @param datetime - A datetime string in ISO format
 * @param timezone - A timezone string
 * @returns A string representing the datetime in a human-readable format (e.g. "01/01/21 - 12:00 AM")
 * @example formatStopTime('2021-01-01T00:00:00.000Z', 'America/New_York')
 * // returns '01/01/21 - 12:00 AM'
 * @example formatStopTime('2021-01-01T00:00:00.000Z', 'America/Los_Angeles')
 * // returns '12/31/20 - 04:00 PM'
 */
export const formatStopTime = (datetime: string, timezone: string): string => {
  return DateTime.fromISO(datetime, { zone: timezone }).toFormat(
    'LL/dd/yy - hh:mm a',
  )
}

export const dateFromDatetime = (
  datetime: string,
  timezone: string,
): string => {
  return DateTime.fromISO(datetime, { zone: timezone }).toFormat('yyyy-MM-dd')
}

export const timeFromDatetime = (
  datetime: string,
  timezone: string,
): string => {
  return DateTime.fromISO(datetime, { zone: timezone }).toFormat('hh:mm a')
}

/**
 * Builds an ISO datetime string from separate date and time components
 * @param dateISO - Date in ISO format (e.g. "2025-09-17")
 * @param timeHHMMA - Time in hh:mm a format (e.g. "04:00 PM")
 * @param timezone - Timezone string (e.g. "America/New_York")
 * @returns ISO datetime string (e.g. "2025-09-17T16:00:00Z")
 */
export const buildDateTime = (
  dateISO: string,
  timeHHMMA: string,
  timezone: string,
): string => {
  if (!dateISO || !timeHHMMA || !timezone) {
    throw new Error('Date, time, and timezone are all required')
  }

  const hhmmTime = convertTo24Hour(timeHHMMA)

  // Create datetime in the specified timezone
  const dateTimeString = `${dateISO}T${hhmmTime}:00`
  const dateTime = DateTime.fromISO(dateTimeString, { zone: timezone })

  if (!dateTime.isValid) {
    throw new Error(
      `Invalid datetime: ${dateTimeString} in timezone ${timezone}`,
    )
  }

  // Convert to UTC and return as ISO string
  return dateTime.toUTC().toISO()
}

/**
 * Converts 12-hour time format (e.g. "07:45 AM") to 24-hour format (e.g. "07:45")
 * @param time12h - Time in 12-hour format with AM/PM (e.g. "07:45 AM", "11:30 PM")
 * @returns Time in 24-hour format (e.g. "07:45", "23:30")
 */
export const convertTo24Hour = (time12h: string): string => {
  return DateTime.fromFormat(time12h.trim(), 'hh:mm a').toFormat('HH:mm')
}

export function time12hTo24hString(displayTime: string): string | null {
  if (!displayTime) return null

  const dt = DateTime.fromFormat(displayTime.trim(), 'hh:mm a', { zone: 'utc' })
  if (!dt.isValid) return null

  return dt.toFormat('HH:mm:ss')
}

export function time24hTo12hString(isoTime: string): string | null {
  if (!isoTime) return null

  const dt = DateTime.fromFormat(isoTime.trim(), 'HH:mm:ss', { zone: 'utc' })
  if (!dt.isValid) return null

  return dt.toFormat('hh:mm a')
}

export function hours12To24Number(
  hour12: number,
  meridian: 'AM' | 'PM',
): number {
  if (hour12 === 12) {
    return meridian === 'AM' ? 0 : 12
  }
  return meridian === 'AM' ? hour12 : hour12 + 12
}

export function hours24To12Number(hour24: number): number {
  if (hour24 === 0) return 12
  if (hour24 > 12) return hour24 - 12
  return hour24
}

export function getMeridianFrom24Hour(hour24: number): 'AM' | 'PM' {
  return hour24 < 12 ? 'AM' : 'PM'
}
